// RefereePanel — extracted from TournamentDetail (Iter 30 refactor).
// Fetches scoring formats + components + tournament registrations/decks,
// lists live matches, exposes score/undo/active-bey actions, and hands
// off UI to RefereeScoringPad (which chooses the artwork variant).
import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { t } from "../../lib/i18n";
import { toast } from "sonner";
import RefereeScoringPad from "./RefereeScoringPad";
import BeyPongRefereePad from "./BeyPongRefereePad";
import MatchTimeline from "../../components/MatchTimeline";

export default function RefereePanel({ tournamentId, tt, matches, players, reload }) {
  const [formats, setFormats] = useState(null);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [components, setComponents] = useState([]);
  // Iter 28/29 — restrict the referee's "Active Bey" dropdown to the beys the
  // player actually registered for THIS tournament (from their picked deck),
  // not the entire global blade library. `regsLoaded` gates the fallback so
  // we never widen the picker while the registrations request is still in
  // flight (would otherwise briefly expose all 140 library options).
  const [registrations, setRegistrations] = useState([]);
  const [decksById, setDecksById] = useState({});
  const [regsLoaded, setRegsLoaded] = useState(false);
  const [regsError, setRegsError] = useState(false);
  const [regsReloadTick, setRegsReloadTick] = useState(0);

  useEffect(() => {
    api.get("/scoring-formats").then((r) => setFormats(r.data)).catch(() => {});
    api.get("/components").then((r) => setComponents(r.data)).catch(() => {});
    if (tournamentId) {
      let cancelled = false;
      setRegsLoaded(false);
      setRegsError(false);
      api.get(`/tournaments/${tournamentId}/registrations`)
        .then(async (r) => {
          if (cancelled) return;
          const regs = r.data || [];
          setRegistrations(regs);
          const uniqueDeckIds = [...new Set(regs.map((x) => x.deck_id).filter(Boolean))];
          const decks = await Promise.all(
            uniqueDeckIds.map((id) => api.get(`/decks/${id}`).then((res) => res.data).catch(() => null))
          );
          if (cancelled) return;
          const map = {};
          decks.forEach((d) => { if (d) map[d.id] = d; });
          setDecksById(map);
          setRegsLoaded(true);
        })
        .catch(() => {
          if (!cancelled) { setRegsError(true); setRegsLoaded(true); }
        });
      return () => { cancelled = true; };
    }
  }, [tournamentId, regsReloadTick]);

  const fmtKey = tt.scoring_format || "polish_bladers";
  const fmt = formats?.[fmtKey];
  // Iter 34 — knockout matches in a group_knockout tournament may use a
  // different threshold. Resolve per-selected-match; fall back to the
  // tournament's `score_limit`.
  const limitFor = (m) => {
    if (!m) return tt.score_limit || 5;
    if (m.stage === "knockout" && tt.knockout_score_limit) {
      return tt.knockout_score_limit;
    }
    return tt.score_limit || 5;
  };
  const pmap = Object.fromEntries(players.map((p) => [p.id, p]));

  const liveMatches = (matches || [])
    .filter((m) => m.player_a_id && m.player_b_id)
    .sort((a, b) => {
      const aDone = a.status === "completed" ? 1 : 0;
      const bDone = b.status === "completed" ? 1 : 0;
      if (aDone !== bDone) return aDone - bDone;
      return (a.round || 0) - (b.round || 0) || (a.slot || 0) - (b.slot || 0);
    });

  const selected = liveMatches.find((m) => m.id === selectedMatchId)
    || liveMatches.find((m) => m.status !== "completed")
    || liveMatches[0];

  if (!selected) {
    return <div className="text-white/40 font-mono text-sm py-10 text-center">{t.bracketEmpty}</div>;
  }

  const a = pmap[selected.player_a_id];
  const b = pmap[selected.player_b_id];

  const recordFinish = async (playerId, finishKey) => {
    setBusy(true);
    try {
      await api.post(`/matches/${selected.id}/finish`, { player_id: playerId, finish_type: finishKey });
      await reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    } finally { setBusy(false); }
  };
  const undo = async () => {
    setBusy(true);
    try {
      await api.post(`/matches/${selected.id}/undo_finish`);
      await reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    } finally { setBusy(false); }
  };

  const forceComplete = async () => {
    setBusy(true);
    try {
      await api.post(`/matches/${selected.id}/force_complete`);
      await reload();
      return { ok: true };
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || t.failed;
      toast.error(msg);
      return { ok: false, error: msg };
    } finally { setBusy(false); }
  };

  const setActiveBey = async (playerId, beyId) => {
    try {
      await api.put(`/matches/${selected.id}/active-bey`, {
        player_id: playerId,
        bey_id: beyId || null,
      });
      await reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    }
  };

  const finishEntries = fmt ? Object.entries(fmt.finishes) : [];
  // Beys the referee can attribute to are the blade-like components. Keep
  // the list compact by filtering out ratchet/bit which are never listed as
  // the "active Bey".
  const allBeyOptions = components
    .filter((c) => ["blade", "main_blade"].includes(c.type))
    .sort((a2, b2) => a2.name.localeCompare(b2.name));
  const componentsById = Object.fromEntries(components.map((c) => [c.id, c]));

  // Iter 28/29 — per-player option list built from the player's registered
  // deck. Falls back to the full library ONLY once we have confirmed
  // registrations loaded and the player either has no registration or a
  // registration with deck_id=null. While `regsLoaded` is false we return an
  // empty list so the referee cannot accidentally attribute a Bey that
  // isn't in the deck.
  const beysForPlayer = (playerId) => {
    if (!playerId) return [];
    if (!regsLoaded) return [];
    if (regsError) return [];
    const reg = registrations.find((r) => r.player_id === playerId);
    if (!reg) return allBeyOptions; // confirmed unregistered → allow full lib
    if (!reg.deck_id) return allBeyOptions; // registered without deck → full lib
    const deck = decksById[reg.deck_id];
    if (!deck) return [];
    if (!deck.beys || deck.beys.length === 0) return allBeyOptions;
    const seen = new Set();
    const opts = [];
    deck.beys.forEach((bey, idx) => {
      const primary = bey.blade || bey.over_blade;
      if (!primary || seen.has(primary)) return;
      seen.add(primary);
      const comp = componentsById[primary];
      opts.push({ id: primary, name: comp ? comp.name : `Bey ${idx + 1}` });
    });
    return opts;
  };
  const beyOptionsA = beysForPlayer(selected.player_a_id);
  const beyOptionsB = beysForPlayer(selected.player_b_id);

  return (
    <div data-testid="referee-panel" className="space-y-3">
      {regsError && (
        <div
          data-testid="ref-regs-error"
          className="flex items-center gap-3 border border-[#FF0055]/40 bg-[#FF0055]/5 px-3 py-2"
        >
          <span className="text-[10px] uppercase tracking-[0.25em] font-mono font-bold text-[#FF0055]">
            ! {t.regsLoadError || "Failed to load registered decks — Active Bey list is empty."}
          </span>
          <button
            onClick={() => setRegsReloadTick((n) => n + 1)}
            data-testid="ref-regs-retry"
            className="ml-auto px-2 py-1 border border-[#FF0055]/40 text-[10px] uppercase tracking-[0.2em] font-bold text-[#FF0055] hover:bg-[#FF0055]/10"
          >
            {t.retry || "Retry"}
          </button>
        </div>
      )}
      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
      <div className="space-y-1.5 max-h-[600px] overflow-y-auto">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#00E5FF] mb-2 font-mono">
          // {t.matches} ({liveMatches.length})
        </div>
        {liveMatches.map((m) => {
          const ma = pmap[m.player_a_id];
          const mb = pmap[m.player_b_id];
          const done = m.status === "completed";
          const isLive = m.status === "live";
          const isSel = m.id === selected.id;
          return (
            <button
              key={m.id}
              onClick={() => setSelectedMatchId(m.id)}
              data-testid={`ref-match-${m.id}`}
              className={`w-full text-left border px-3 py-2 text-xs font-mono transition-colors ${
                isSel ? "border-[#FF0055] bg-[#FF0055]/10" :
                done ? "border-white/10 bg-[#0C0C14] text-white/40" :
                isLive ? "border-[#E2FF00]/40 bg-[#E2FF00]/5" :
                "border-white/15 bg-[#0C0C14] hover:border-[#00E5FF]/40"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{ma?.handle || "TBD"}</span>
                <span className="text-white/40">{m.score_a}-{m.score_b}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{mb?.handle || "TBD"}</span>
                <span className="text-[9px] text-white/30 uppercase">
                  {m.is_tiebreaker ? "TB" : m.stage === "group" ? `G${(m.group_index ?? 0) + 1}` : `R${m.round}`}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {fmtKey === "bey_pong" ? (
        <BeyPongRefereePad
          selected={selected}
          playerA={a}
          playerB={b}
          componentsById={componentsById}
          deckA={(() => { const r = registrations.find((x) => x.player_id === selected.player_a_id); return r ? decksById[r.deck_id] : null; })()}
          deckB={(() => { const r = registrations.find((x) => x.player_id === selected.player_b_id); return r ? decksById[r.deck_id] : null; })()}
          reload={reload}
        />
      ) : (
        <RefereeScoringPad
          selected={selected}
          playerA={a}
          playerB={b}
          limit={limitFor(selected)}
          finishEntries={finishEntries}
          busy={busy}
          recordFinish={recordFinish}
          setActiveBey={setActiveBey}
          beyOptionsA={beyOptionsA}
          beyOptionsB={beyOptionsB}
          isPolska={fmtKey === "beyblade_polska"}
          isPokeblade={fmtKey === "pokeblade"}
          isStandard={fmtKey === "polish_bladers"}
          undo={undo}
          forceComplete={forceComplete}
          goNext={() => {
            const notDone = liveMatches.filter((m) => m.status !== "completed" && m.id !== selected.id);
            if (notDone.length) setSelectedMatchId(notDone[0].id);
          }}
        />
      )}
      </div>
      {/* Iter 32 — visual replay timeline. Not applicable to BEY-PONG which
          uses its own set/cycle state machine + Undo button. */}
      {fmtKey !== "bey_pong" && (
        <MatchTimeline
          match={selected}
          playerA={a}
          playerB={b}
          finishTypes={fmt?.finishes || {}}
          busy={busy}
          onRewind={reload}
        />
      )}
    </div>
  );
}
